import {
  validatePassword,
  validatePasswordMatch,
} from './passwordValidation.utils';

// Define types for validation results
export type ValidationError = string | undefined;
export type ValidationErrors = Record<string, ValidationError>;

// Generic field validators
export const validateRequired = (
  value: string,
  fieldName: string,
): ValidationError => {
  return !value.trim() ? `${fieldName} is required` : undefined;
};

export const validateMinLength = (
  value: string,
  minLength: number,
  fieldName: string,
): ValidationError => {
  return value.trim().length < minLength
    ? `${fieldName} should be at least ${minLength} characters`
    : undefined;
};

export const validateEmail = (value: string): ValidationError => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return !emailRegex.test(value)
    ? 'Please enter a valid email address'
    : undefined;
};

export const validateDate = (value: string): ValidationError => {
  if (!value) return undefined;

  const date = new Date(value);
  const today = new Date();

  if (isNaN(date.getTime())) {
    return 'Please enter a valid date';
  }

  if (date > today) {
    return 'Date cannot be in the future';
  }

  return undefined;
};

// Field-specific validators
export const validateName = (
  value: string,
  fieldName: string,
): ValidationError => {
  const requiredError = validateRequired(value, fieldName);
  if (requiredError) return requiredError;

  return validateMinLength(value, 2, fieldName);
};

// Form field validator - returns object with all errors
export const validateFormFieldSignUp = (
  name: string,
  value: string,
  form: Record<string, string> = {},
): ValidationError => {
  switch (name) {
    case 'firstName':
      return validateName(value, 'First name');

    case 'lastName':
      return validateName(value, 'Last name');

    case 'email':
      const requiredError = validateRequired(value, 'Email');
      if (requiredError) return requiredError;
      return validateEmail(value);

    case 'password':
      const passwordRequiredError = validateRequired(value, 'Password');
      if (passwordRequiredError) return passwordRequiredError;
      const passwordValidation = validatePassword(value);
      if (!passwordValidation.isValid) {
        return passwordValidation.error;
      }
      return undefined;

    case 'confirmPassword':
      const confirmRequiredError = validateRequired(value, 'Confirm password');
      if (confirmRequiredError) return confirmRequiredError;
      const matchValidation = validatePasswordMatch(form.password || '', value);
      if (!matchValidation.isValid) {
        return matchValidation.error;
      }
      return undefined;

    case 'dateOfBirth':
      return validateDate(value);

    default:
      return undefined;
  }
};

// Validate all fields in a form at once
export const validateForm = (
  form: Record<string, string>,
): ValidationErrors => {
  const errors: ValidationErrors = {};

  Object.entries(form).forEach(([field, value]) => {
    const error = validateFormFieldSignUp(field, value, form);
    if (error) {
      errors[field] = error;
    }
  });

  return errors;
};
